import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import AdminNavigation from '../../components/ui/AdminNavigation';
import {
  AlertCircle, CheckCircle, XCircle, RefreshCw, Link2, Link2Off,
  Search, UserCheck, UserX, Shield, Clock, Eye
} from 'lucide-react';

const Badge = ({ children, color }) => {
  const colors = {
    green: 'bg-green-100 text-green-800',
    red: 'bg-red-100 text-red-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    blue: 'bg-blue-100 text-blue-800',
    gray: 'bg-gray-100 text-gray-600',
    purple: 'bg-purple-100 text-purple-800',
    orange: 'bg-orange-100 text-orange-800',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colors?.[color] || colors?.gray}`}>
      {children}
    </span>
  );
};

const getRoleBadgeColor = (role) => {
  if (!role) return 'gray';
  if (role === 'super_admin') return 'purple';
  if (role === 'admin') return 'blue';
  if (role === 'chauffeur') return 'green';
  if (role === 'agent' || role === 'agent_abidjan') return 'orange';
  return 'gray';
};

const AdminUsersDiagnostic = () => {
  const [diagnosticData, setDiagnosticData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterIssues, setFilterIssues] = useState('all');
  const [linking, setLinking] = useState(null);
  const [toast, setToast] = useState(null);
  const [stats, setStats] = useState({ total: 0, linked: 0, unlinked: 0, inactive: 0, issues: 0 });
  const [expandedRow, setExpandedRow] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadDiagnosticData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // 1. Load all employees
      const { data: employees, error: empError } = await supabase
        ?.from('employees')
        ?.select('id, name, email, role, is_active, auth_user_id, created_at')
        ?.order('created_at', { ascending: false });

      if (empError) throw empError;

      // 2. Load user_profiles (RBAC roles)
      const { data: profiles } = await supabase
        ?.from('user_profiles')
        ?.select('id, email, full_name, role, is_active, last_login_at, created_at');

      // 3. Load admin_users
      const { data: adminUsers } = await supabase
        ?.from('admin_users')
        ?.select('id, email, full_name, role, is_active, last_login_at, created_at');

      // Build lookup maps
      const authById = {};
      const authByEmail = {};

      (profiles || [])?.forEach(p => {
        authById[p?.id] = { ...p, source: 'user_profiles' };
        if (p?.email) authByEmail[p?.email?.toLowerCase()] = { ...p, source: 'user_profiles' };
      });

      (adminUsers || [])?.forEach(a => {
        if (!authById?.[a?.id]) authById[a?.id] = { ...a, source: 'admin_users' };
        if (a?.email && !authByEmail?.[a?.email?.toLowerCase()]) {
          authByEmail[a?.email?.toLowerCase()] = { ...a, source: 'admin_users' };
        }
      });

      // 4. Build diagnostic rows
      const rows = (employees || [])?.map(emp => {
        const authByIdMatch = emp?.auth_user_id ? authById?.[emp?.auth_user_id] : null;
        const authByEmailMatch = emp?.email ? authByEmail?.[emp?.email?.toLowerCase()] : null;

        // Determine RBAC role from user_profiles
        const rbacRole = authByIdMatch?.role || authByEmailMatch?.role || null;
        const rbacSource = authByIdMatch?.source || authByEmailMatch?.source || null;

        const issues = [];

        if (!emp?.auth_user_id) {
          issues?.push('Pas de auth_user_id lié — connexion impossible');
        } else if (!authByIdMatch) {
          issues?.push('auth_user_id introuvable dans Auth (compte supprimé?)');
        }

        if (!emp?.is_active && emp?.auth_user_id && authByIdMatch?.is_active) {
          issues?.push('Employé inactif mais compte Auth actif — accès bloqué côté app');
        }

        if (emp?.is_active && !emp?.auth_user_id && emp?.role === 'chauffeur') {
          issues?.push('Chauffeur actif sans compte Auth — ne peut pas se connecter');
        }

        if (emp?.email && !emp?.auth_user_id && authByEmailMatch) {
          issues?.push(`Email trouvé dans Auth (${authByEmailMatch?.source}) mais auth_user_id non lié`);
        }

        if (emp?.role === 'chauffeur' && rbacRole && rbacRole !== 'chauffeur') {
          issues?.push(`Rôle incohérent: employees="${emp?.role}" mais user_profiles="${rbacRole}"`);
        }

        if (emp?.is_active && authByIdMatch?.is_active === false) {
          issues?.push('Employé actif mais profil Auth désactivé');
        }

        return {
          employeeId: emp?.id,
          name: emp?.name,
          email: emp?.email,
          role: emp?.role,
          rbacRole,
          rbacSource,
          isActive: emp?.is_active,
          authUserId: emp?.auth_user_id,
          authRecord: authByIdMatch || null,
          authLinked: !!authByIdMatch,
          authFoundByEmail: !authByIdMatch && !!authByEmailMatch,
          authFoundByEmailRecord: authByEmailMatch,
          lastLogin: authByIdMatch?.last_login_at || authByEmailMatch?.last_login_at || null,
          authStatus: authByIdMatch
            ? (authByIdMatch?.is_active !== false ? 'Actif' : 'Désactivé')
            : (authByEmailMatch ? 'Trouvé par email (non lié)' : 'Introuvable'),
          issues,
          hasIssues: issues?.length > 0,
          createdAt: emp?.created_at,
        };
      });

      setDiagnosticData(rows);

      const total = rows?.length;
      const linked = rows?.filter(r => r?.authLinked)?.length;
      const unlinked = rows?.filter(r => !r?.authLinked)?.length;
      const inactive = rows?.filter(r => !r?.isActive)?.length;
      const issues = rows?.filter(r => r?.hasIssues)?.length;
      setStats({ total, linked, unlinked, inactive, issues });

    } catch (err) {
      console.error('Diagnostic load error:', err);
      setError(err?.message || 'Erreur lors du chargement du diagnostic');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDiagnosticData();
  }, [loadDiagnosticData]);

  // Link auth user to employee by email
  const handleLinkByEmail = async (row) => {
    if (!row?.authFoundByEmailRecord) return;
    setLinking(row?.employeeId);
    try {
      const { error } = await supabase
        ?.from('employees')
        ?.update({ auth_user_id: row?.authFoundByEmailRecord?.id })
        ?.eq('id', row?.employeeId);

      if (error) throw error;

      showToast(`✅ ${row?.name} lié à Auth (${row?.authFoundByEmailRecord?.id?.slice(0, 8)}…)`);
      await loadDiagnosticData();
    } catch (err) {
      showToast(`❌ Erreur: ${err?.message}`, 'error');
    } finally {
      setLinking(null);
    }
  };

  // Toggle employee active status
  const handleToggleActive = async (row) => {
    try {
      const { error } = await supabase
        ?.from('employees')
        ?.update({ is_active: !row?.isActive })
        ?.eq('id', row?.employeeId);

      if (error) throw error;

      showToast(`${row?.name} est maintenant ${!row?.isActive ? 'actif' : 'inactif'}`);
      await loadDiagnosticData();
    } catch (err) {
      showToast(`❌ Erreur: ${err?.message}`, 'error');
    }
  };

  const filtered = diagnosticData?.filter(row => {
    const matchSearch =
      row?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      (row?.email && row?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase())) ||
      (row?.authUserId && row?.authUserId?.includes(searchTerm));
    const matchFilter =
      filterIssues === 'all' ||
      (filterIssues === 'issues' && row?.hasIssues) ||
      (filterIssues === 'linked' && row?.authLinked) ||
      (filterIssues === 'unlinked' && !row?.authLinked) ||
      (filterIssues === 'inactive' && !row?.isActive);
    return matchSearch && matchFilter;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr)?.toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Shield className="text-purple-600" size={32} />
              Diagnostic Utilisateurs
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Détecte les incohérences entre Supabase Auth et la table Employees — Rôles métier ADT Express
            </p>
          </div>
          <button
            onClick={loadDiagnosticData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {[
            { label: 'Total employés', value: stats?.total, color: 'blue', icon: <UserCheck size={20} /> },
            { label: 'Auth liés', value: stats?.linked, color: 'green', icon: <Link2 size={20} /> },
            { label: 'Auth non liés', value: stats?.unlinked, color: 'yellow', icon: <Link2Off size={20} /> },
            { label: 'Inactifs', value: stats?.inactive, color: 'gray', icon: <UserX size={20} /> },
            { label: 'Problèmes', value: stats?.issues, color: 'red', icon: <AlertCircle size={20} /> },
          ]?.map((s) => (
            <div key={s?.label} className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className={`text-${s?.color}-600 mb-1`}>{s?.icon}</div>
              <div className="text-2xl font-bold text-gray-900">{s?.value}</div>
              <div className="text-xs text-gray-500">{s?.label}</div>
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-48">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, email ou auth_user_id..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterIssues}
            onChange={(e) => setFilterIssues(e?.target?.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">Tous</option>
            <option value="issues">⚠️ Avec problèmes</option>
            <option value="linked">✅ Auth liés</option>
            <option value="unlinked">❌ Auth non liés</option>
            <option value="inactive">🔴 Inactifs</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-gray-500">Analyse en cours...</span>
            </div>
          ) : filtered?.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <Shield size={40} className="mx-auto mb-3 text-gray-300" />
              <p>Aucun résultat trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['Nom / Email', 'Rôle (employees)', 'Rôle RBAC', 'Auth User ID', 'Auth lié', 'Actif', 'Dernière connexion', 'Problèmes détectés', 'Actions']?.map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filtered?.map((row) => (
                    <React.Fragment key={row?.employeeId}>
                      <tr className={row?.hasIssues ? 'bg-red-50/30' : 'hover:bg-gray-50'}>
                        {/* Nom / Email */}
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 text-sm">{row?.name}</div>
                          <div className="text-xs text-gray-500">{row?.email || '—'}</div>
                        </td>

                        {/* Rôle employees */}
                        <td className="px-4 py-3">
                          <Badge color={getRoleBadgeColor(row?.role)}>
                            {row?.role || '—'}
                          </Badge>
                        </td>

                        {/* Rôle RBAC (user_profiles) */}
                        <td className="px-4 py-3">
                          {row?.rbacRole ? (
                            <div>
                              <Badge color={getRoleBadgeColor(row?.rbacRole)}>
                                {row?.rbacRole}
                              </Badge>
                              <div className="text-xs text-gray-400 mt-0.5">{row?.rbacSource}</div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Non défini</span>
                          )}
                        </td>

                        {/* Auth User ID */}
                        <td className="px-4 py-3">
                          {row?.authUserId ? (
                            <div>
                              <span
                                className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded cursor-pointer hover:bg-gray-200"
                                title={row?.authUserId}
                                onClick={() => setExpandedRow(expandedRow === row?.employeeId ? null : row?.employeeId)}
                              >
                                {row?.authUserId?.slice(0, 8)}…
                              </span>
                              {expandedRow === row?.employeeId && (
                                <div className="text-xs font-mono text-gray-500 mt-1 break-all max-w-32">
                                  {row?.authUserId}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-red-500 italic">Non lié</span>
                          )}
                        </td>

                        {/* Auth lié */}
                        <td className="px-4 py-3">
                          {row?.authLinked ? (
                            <span className="flex items-center gap-1 text-green-700 text-xs font-medium">
                              <CheckCircle size={14} /> Oui
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-600 text-xs font-medium">
                              <XCircle size={14} /> Non
                            </span>
                          )}
                        </td>

                        {/* Actif */}
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleToggleActive(row)}
                            title="Cliquer pour changer le statut"
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-all hover:opacity-80 ${
                              row?.isActive ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-red-100 text-red-800 hover:bg-red-200'
                            }`}
                          >
                            {row?.isActive ? <><CheckCircle size={12} /> Oui</> : <><XCircle size={12} /> Non</>}
                          </button>
                        </td>

                        {/* Dernière connexion */}
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock size={12} />
                            {formatDate(row?.lastLogin)}
                          </span>
                        </td>

                        {/* Problèmes détectés */}
                        <td className="px-4 py-3 max-w-xs">
                          {row?.issues?.length === 0 ? (
                            <span className="text-xs text-green-600 flex items-center gap-1">
                              <CheckCircle size={12} /> Aucun problème
                            </span>
                          ) : (
                            <ul className="space-y-0.5">
                              {row?.issues?.map((issue, i) => (
                                <li key={i} className="text-xs text-red-600 flex items-start gap-1">
                                  <AlertCircle size={11} className="flex-shrink-0 mt-0.5" />
                                  {issue}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            {row?.authFoundByEmail && !row?.authLinked && (
                              <button
                                onClick={() => handleLinkByEmail(row)}
                                disabled={linking === row?.employeeId}
                                className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors whitespace-nowrap"
                              >
                                {linking === row?.employeeId ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                                ) : (
                                  <Link2 size={12} />
                                )}
                                Lier Auth
                              </button>
                            )}
                            {row?.authUserId && (
                              <button
                                onClick={() => setExpandedRow(expandedRow === row?.employeeId ? null : row?.employeeId)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
                              >
                                <Eye size={12} />
                                {expandedRow === row?.employeeId ? 'Masquer ID' : 'Voir ID'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-800 mb-2">📖 Légende & Explication des colonnes</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-blue-700">
            <div>• <strong>Rôle (employees)</strong> : rôle métier dans la table employees (chauffeur, admin, aide…)</div>
            <div>• <strong>Rôle RBAC</strong> : rôle dans user_profiles (super_admin, admin, chauffeur…) — source du contrôle d'accès</div>
            <div>• <strong>Auth User ID</strong> : identifiant dans Supabase Auth (auth.users) — cliquer pour voir l'ID complet</div>
            <div>• <strong>Auth lié</strong> : l'auth_user_id correspond à un enregistrement Auth valide</div>
            <div>• <strong>Actif</strong> : cliquable pour activer/désactiver directement</div>
            <div>• <strong>Lier Auth</strong> : disponible si l'email correspond à un compte Auth non lié</div>
            <div>• <strong>Chauffeur inactif</strong> = ne peut pas se connecter sur /driver-login</div>
            <div>• <strong>Auth non lié</strong> = connexion impossible même avec bon mot de passe</div>
          </div>
        </div>

        {/* How login works */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-green-800 mb-2">🚗 Login Chauffeur (/driver-login)</h3>
            <ol className="text-xs text-green-700 space-y-1 list-decimal list-inside">
              <li>Authentification Supabase Auth (email/mot de passe)</li>
              <li>Recherche dans employees par auth_user_id</li>
              <li>Vérification role = chauffeur</li>
              <li>Vérification is_active = true</li>
              <li>Si OK → Dashboard chauffeur</li>
              <li>Sinon → Message d'erreur détaillé</li>
            </ol>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-purple-800 mb-2">🔐 Login Admin (/admin-login)</h3>
            <ol className="text-xs text-purple-700 space-y-1 list-decimal list-inside">
              <li>Authentification Supabase Auth (email/mot de passe)</li>
              <li>Recherche dans user_profiles par auth_user_id</li>
              <li>Vérification role = super_admin ou admin</li>
              <li>Vérification is_active = true</li>
              <li>Si OK → Dashboard admin</li>
              <li>Sinon → Accès refusé avec raison</li>
            </ol>
          </div>
        </div>
      </div>
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white transition-all ${
          toast?.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {toast?.type === 'success' ? <CheckCircle size={18} /> : <XCircle size={18} />}
          <span className="text-sm font-medium">{toast?.message}</span>
          <button onClick={() => setToast(null)} className="ml-2 text-white/80 hover:text-white">✕</button>
        </div>
      )}
    </div>
  );
};

export default AdminUsersDiagnostic;
